import { useState } from "react";
import { Copy, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, FormError, SubmitButton } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

export function TempMemberButton({ endpoint, defaultRole = "mechanic", onDone, testId = "temp-member-button" }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", role: defaultRole });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const close = () => { setOpen(false); setResult(null); setF({ name: "", email: "", phone: "", role: defaultRole }); setError(""); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      setResult((await api.post(endpoint, f)).data);
      onDone?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid={testId}><KeyRound className="mr-1.5 h-4 w-4" />{t("b8.temp.button")}</Button>
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid="temp-member-dialog">
          <DialogHeader><DialogTitle>{t("b8.temp.title")}</DialogTitle><DialogDescription>{t("b8.temp.hint")}</DialogDescription></DialogHeader>
          {result ? (
            <div className="space-y-3" data-testid="temp-member-result">
              {result.temporary_password ? (<>
                <p className="text-sm">{t("b8.temp.once", { email: result.email })}</p>
                <div className="flex items-center gap-2"><code className="flex-1 rounded-lg bg-slate-100 px-3 py-2 font-mono text-lg" data-testid="temp-password-value">{result.temporary_password}</code>
                  <Button size="sm" onClick={() => navigator.clipboard.writeText(result.temporary_password).then(() => toast.success(t("p7.share.copied")))} data-testid="temp-password-copy"><Copy className="h-4 w-4" /></Button></div>
              </>) : <p className="text-sm" data-testid="temp-member-existing">{t("b8.temp.existing", { email: result.email })}</p>}
              <Button className="w-full" onClick={close} data-testid="temp-member-done">{t("b8.temp.done")}</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <FormError message={error} />
              <Field label={t("p7.signup.fullName")} htmlFor="tm-name"><Input id="tm-name" required minLength={2} value={f.name} onChange={set("name")} data-testid="temp-member-name" /></Field>
              <Field label={t("auth.email")} htmlFor="tm-email"><Input id="tm-email" type="email" required value={f.email} onChange={set("email")} data-testid="temp-member-email" /></Field>
              <Field label={t("p7.signup.phone")} htmlFor="tm-phone"><Input id="tm-phone" value={f.phone} onChange={set("phone")} data-testid="temp-member-phone" /></Field>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger data-testid="temp-member-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="garage_manager">{t("status.garage_manager")}</SelectItem><SelectItem value="mechanic">{t("status.mechanic")}</SelectItem></SelectContent>
              </Select>
              <SubmitButton busy={busy} className="w-full" data-testid="temp-member-submit">{t("b8.temp.create")}</SubmitButton>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
